import { Box, Checkbox, Popover, Portal, Stack } from '@chakra-ui/react';
import React, { Fragment, useEffect, useState } from 'react';
import { FilterPopoverButton, FilterPopoverContent } from 'src/components/atoms';
import { ListingFilterItemProps } from '.';
import { useAttributeSelector } from 'src/contexts/AttributeSelector';
import { AttributeOption } from 'src/api/v1-api';
import { resolveAttributeSource } from './utils';

const TreeCheckboxFilter = ({
  attributeId,
  isMobileView = false,
}: ListingFilterItemProps): JSX.Element => {
  const defaultValue: number[] | string[] | boolean[] = [];
  const [inputValue, setInputValue] = useState<(number | string | boolean)[]>(defaultValue);
  const {
    attributes,
    categoryInFilters,
    selectedAttributes,
    setSelectedAttributes,
    stagedAttributes,
    setStagedAttributes,
  } = useAttributeSelector();
  const attributeSource = resolveAttributeSource(categoryInFilters?.attributes, attributes);
  const attribute = attributeSource.find((a) => a.name === attributeId);

  useEffect(() => {
    if (attributeId in selectedAttributes) {
      setInputValue(selectedAttributes[attributeId]);
    }
  }, [selectedAttributes, attributeId]);

  const attributeOptions =
    attribute?.options && Array.isArray(attribute.options) ? attribute.options : [];

  const topLevelAttributeOptions = attributeOptions.filter((option) => !option.parentId);
  // Sanitize helper: remove accidental empty string markers while preserving 0 / false
  const sanitizeValues = (values: (number | string | boolean)[]) =>
    values.filter((v) => !(typeof v === 'string' && v.trim() === ''));

  // Ensure any preloaded selection is sanitized
  useEffect(() => {
    if (attributeId in selectedAttributes) {
      setInputValue(sanitizeValues(selectedAttributes[attributeId]));
    }
  }, [selectedAttributes, attributeId]);

  const noFilter =
    !selectedAttributes[attributeId] ||
    selectedAttributes[attributeId].length === 0 ||
    (selectedAttributes[attributeId].length === 1 && selectedAttributes[attributeId][0] === '');

  // ---------- Tree helpers ----------
  const getChildren = (option: AttributeOption) =>
    attributeOptions.filter((o) => o.parentId === option.id);
  const isLeaf = (option: AttributeOption) => getChildren(option).length === 0;

  const getDescendants = (option: AttributeOption): AttributeOption[] => {
    const children = getChildren(option);
    return [...children, ...children.flatMap((c) => getDescendants(c))];
  };

  const getLeafDescendants = (option: AttributeOption) => getDescendants(option).filter(isLeaf);

  const selectedSet = new Set<string | number | boolean>(inputValue);

  // isChecked logic: parent considered fully selected when explicitly selected AND (all leaves selected OR single-leaf chain special-case)
  const isParentFullySelected = (option: AttributeOption) => {
    if (isLeaf(option)) return selectedSet.has(option.value);
    const leaves = getLeafDescendants(option);
    const explicit = selectedSet.has(option.value);
    const any = leaves.some((l) => selectedSet.has(l.value));
    const all = leaves.length > 0 && leaves.every((l) => selectedSet.has(l.value));
    // Full when:
    // - parent explicitly selected AND (no leaves OR all leaves selected OR none selected)
    //   (none selected means user wants only the parent category)
    if (explicit && (leaves.length === 0 || all || !any)) return true;
    // Single-chain (one leaf) with explicit parent always full regardless of leaf state
    if (explicit && leaves.length === 1) return true;
    return false;
  };

  const isParentPartiallySelected = (option: AttributeOption) => {
    if (isLeaf(option)) return false;
    const leaves = getLeafDescendants(option);
    if (leaves.length === 0) return false;
    // Single-chain (one leaf) never indeterminate (design rule)
    if (leaves.length === 1) return false;
    const any = leaves.some((l) => selectedSet.has(l.value));
    const all = leaves.every((l) => selectedSet.has(l.value));
    // Partial when some but not all leaves selected
    return any && !all;
  };

  const handleCheckboxChange = (option: AttributeOption, isChecked: boolean) => {
    const newSet = new Set(selectedSet);
    if (isChecked) {
      // Selecting parent: add itself + all descendants
      if (!(typeof option.value === 'string' && option.value.trim() === ''))
        newSet.add(option.value);
      if (!isLeaf(option)) {
        getDescendants(option).forEach((d) => {
          if (!(typeof d.value === 'string' && (d.value as string).trim() === ''))
            newSet.add(d.value);
        });
      }
    } else {
      if (isLeaf(option)) {
        newSet.delete(option.value);
      } else {
        // Unselect parent: remove itself + all descendants
        newSet.delete(option.value);
        getDescendants(option).forEach((d) => newSet.delete(d.value));
      }
    }
    newSet.delete('');
    const values = sanitizeValues(Array.from(newSet));
    setInputValue(values);
    setStagedAttributes({
      ...stagedAttributes,
      [attributeId]: values,
    });
  };

  const renderOptions = (options: AttributeOption[], depth = 0) => {
    const pl = depth * 4;
    return options
      .sort((a, b) => a.position - b.position)
      .map((option) => (
        <Fragment key={option.id ?? option.value}>
          <Checkbox
            isChecked={isParentFullySelected(option)}
            isIndeterminate={isParentPartiallySelected(option)}
            colorScheme="primary"
            onChange={(e) => handleCheckboxChange(option, e.target.checked)}
            pl={pl}
          >
            {option.name}
          </Checkbox>
          {getChildren(option).length > 0 && renderOptions(getChildren(option), depth + 1)}
        </Fragment>
      ));
  };

  return isMobileView ? (
    <Box maxHeight="320px" overflowY="auto" overflowX="hidden">
      <Stack as="fieldset" spacing={2}>
        {renderOptions(topLevelAttributeOptions)}
      </Stack>
    </Box>
  ) : (
    <Popover placement="bottom-start">
      <FilterPopoverButton label={attribute?.name} hasFilter={!noFilter} />
      <Portal>
        <FilterPopoverContent
          isCancelDisabled={!(defaultValue !== inputValue)}
          onClickApply={() => {
            setSelectedAttributes({
              ...selectedAttributes,
              [attributeId]: sanitizeValues(inputValue),
            });
          }}
          onClickCancel={() => {
            setInputValue(defaultValue);
            setSelectedAttributes({
              ...selectedAttributes,
              [attributeId]: defaultValue,
            });
          }}
        >
          <Box maxHeight="320px" overflowY="auto" overflowX="hidden">
            <Stack as="fieldset" spacing={2}>
              {renderOptions(topLevelAttributeOptions)}
            </Stack>
          </Box>
        </FilterPopoverContent>
      </Portal>
    </Popover>
  );
};

export default TreeCheckboxFilter;
